import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import { getAllItemsByLocalId } from '../../../../../../../../../../../../../../crud/menu_api/items';
import { GetAllSelectionGroupsByLocalId } from '../../../../../../../../../../../../../../crud/menu_api/selection_groups';
import GroupConfigurationSection from './components/GroupConfigurationSection/GroupConfigurationSection';
import ItemSelectorSection from './components/ItemSelectorSection/ItemSelectorSection';
import ItemsTableSection from './components/ItemTableSection/ItemsTableSection';
import './CreateSelectionGroupModal.scss';

interface SelectionGroupItem {
  item_id: number;
  item_name: string;
  price_modifier: number;
  delivery_price_modifier: number;
  online_price_modifier: number;
  desk_price_modifier: number;
  min_selections: number;
  max_selections: number;
}

interface SelectionGroupData {
  id: string;
  name: string;
  min_selections: number;
  max_selections: number;
  min_variance: number;
  max_variance: number;
  items: SelectionGroupItem[];
}

interface CreateSelectionGroupModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (selectionGroup: SelectionGroupData) => void;
  currentItemName: string;
  editMode?: boolean;
  editData?: SelectionGroupData;
}

const CreateSelectionGroupModal: React.FC<CreateSelectionGroupModalProps> = ({
  show,
  onHide,
  onSave,
  currentItemName,
  editMode = false,
  editData
}) => {
  const [groupName, setGroupName] = useState('');
  const [minSelections, setMinSelections] = useState(0);
  const [maxSelections, setMaxSelections] = useState(1);
  const [minVariance, setMinVariance] = useState(0);
  const [maxVariance, setMaxVariance] = useState(1);
  const [items, setItems] = useState<SelectionGroupItem[]>([]);
  
  // Item search and selection
  const [allItems, setAllItems] = useState<any[]>([]);
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  
  // Categories for filtering
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  
  // Existing groups in the local to validate duplicates
  const [existingGroups, setExistingGroups] = useState<any[]>([]);
  const [loadingExistingGroups, setLoadingExistingGroups] = useState(false);

  
  // Reset form when modal opens or populate with edit data
  useEffect(() => {
    if (show) {
      if (editMode && editData) {
        // Populate form with existing data
        setGroupName(editData.name);
        setMinSelections(editData.min_selections);
        setMaxSelections(editData.max_selections);
        setMinVariance(editData.min_variance);
        setMaxVariance(editData.max_variance);
        setItems(editData.items);
      } else {
        // Reset form for new group
        setGroupName('');
        setMinSelections(0);
        setMaxSelections(1);
        setMinVariance(0);
        setMaxVariance(1);
        setItems([]);
      }
      setSearchQuery('');
      setShowItemSelector(false);
      setSelectedCategories([]);
    }
  }, [show, editMode, editData]);

  // Fetch all items when modal opens
  useEffect(() => {
    if (!show) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;
    setLoadingItems(true);
    getAllItemsByLocalId(localId)
      .then(r => {
        setAllItems(r.data || []);
        // Extract unique categories from items
        const categories = new Map();
        (r.data || []).forEach((item: any) => {
          if (item.category_id && item.category_name) {
            categories.set(item.category_id, {
              category_id: item.category_id,
              category_name: item.category_name
            });
          }
        });
        setAllCategories(Array.from(categories.values()));
      })
      .catch(err => console.error('Error fetching items', err))
      .finally(() => setLoadingItems(false));
  }, [show]);

  // Fetch existing selection groups for this local when modal opens (to validate duplicates)
  useEffect(() => {
    if (!show) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;
    setLoadingExistingGroups(true);
    GetAllSelectionGroupsByLocalId(localId)
      .then(r => setExistingGroups(r.data || []))
      .catch(err => {
        // Log but don't block the user — duplicate check will be best-effort
        console.error('Error fetching selection groups', err);
        setExistingGroups([]);
      })
      .finally(() => setLoadingExistingGroups(false));
  }, [show]);

  // Derive available items (exclude already added items)
  useEffect(() => {
    const existingIds = new Set(items.map(item => item.item_id));
    setAvailableItems(allItems.filter((item: any) => !existingIds.has(item.item_id)));
  }, [allItems, items]);

  // Filter items based on search query and selected categories
  useEffect(() => {
    let base = availableItems || [];
    
    // Filter by categories first
    if (selectedCategories.length > 0) {
      const selectedCategoryIds = selectedCategories.map(cat => cat.value);
      base = base.filter((item: any) => selectedCategoryIds.includes(item.category_id));
    }
    
    // Then filter by search query
    const q = normalize(searchQuery);
    if (!q) { 
      setFilteredItems(base); 
      return; 
    }
    
    const tokens = q.split(' ').filter(Boolean);
    const filtered = base.filter((item: any) => {
      const haystack = normalize(item?.item_name + ' ' + item?.label);
      return tokens.every(tok => haystack.includes(tok));
    });
    setFilteredItems(filtered);
  }, [searchQuery, availableItems, selectedCategories]);

  // Normalization helper (remove accents, spaces, convert to lowercase)
  const normalize = (str: string) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  // Real-time duplicate detection for group name (exclude current edit group's id)
  const isDuplicateName = (() => {
    const name = normalize(groupName || '');
    if (!name) return false;
    return existingGroups.some((g: any) => {
      const gName = normalize(g.selection_group_name || g.name || '');
      if (editMode && editData) {
        // If editing, allow the same name as the group being edited
        const editId = String(editData.id || '');
        const gId = String(g.selection_group_id || g.id || '');
        if (editId && gId && editId === gId) return false;
      }
      return gName === name;
    });
  })();

  const handleAddItem = (item: any) => {
    const newItem: SelectionGroupItem = {
      item_id: item.item_id,
      item_name: item.item_name,
      price_modifier: item.price || 0,
      delivery_price_modifier: item.price_delivery || 0,
      online_price_modifier: item.price_online || 0,
      desk_price_modifier: item.price_desk || 0,
      min_selections: 0,
      max_selections: 1
    };

    setItems([...items, newItem]);
    setShowItemSelector(false);
    toast.success(`${item.item_name} agregado al grupo`);
  };

  const handleAddMultipleItems = (selectedItems: any[]) => {
    const newItems: SelectionGroupItem[] = selectedItems.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price_modifier: item.price || 0,
      delivery_price_modifier: item.price_delivery || 0,
      online_price_modifier: item.price_online || 0,
      desk_price_modifier: item.price_desk || 0,
      min_selections: 0,
      max_selections: 1
    }));

    setItems([...items, ...newItems]);
    setShowItemSelector(false);
    
    const itemNames = selectedItems.map(item => item.item_name).join(', ');
    const message = selectedItems.length === 1 
      ? `${itemNames} agregado al grupo`
      : `${selectedItems.length} items agregados al grupo`;
    toast.success(message);
  };

  const handleAddAllFromCategory = (categoryItems: any[]) => {
    const newItems: SelectionGroupItem[] = categoryItems.map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price_modifier: item.price || 0,
      delivery_price_modifier: item.price_delivery || 0,
      online_price_modifier: item.price_online || 0,
      desk_price_modifier: item.price_desk || 0,
      min_selections: 0,
      max_selections: 1
    }));

    setItems([...items, ...newItems]);
    
    const categoryNames = selectedCategories.map(cat => cat.label).join(', ');
    const message = selectedCategories.length === 1 
      ? `${categoryItems.length} ítems de la categoría "${categoryNames}" agregados al grupo`
      : `${categoryItems.length} ítems de las categorías "${categoryNames}" agregados al grupo`;
    toast.success(message);
  };

  const handleUpdateItem = (itemId: number, field: string, value: number) => {
    setItems(items.map(item => 
      item.item_id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handleBulkUpdateItem = (itemId: number, updates: {[field: string]: number}) => {
    setItems(items.map(item => {
      if (item.item_id === itemId) {
        return { ...item, ...updates };
      }
      return item;
    }));
  };

  const handleDeleteItem = (itemId: number) => {
    const item = items.find(i => i.item_id === itemId);
    setItems(items.filter(item => item.item_id !== itemId));
    toast.success(`${item?.item_name} eliminado del grupo`);
  };

  const handleSave = () => {
    // Validations
    if (!groupName.trim()) {
      toast.error('El nombre del grupo es requerido');
      return;
    }

    if (minSelections < 0) {
      toast.error('Las selecciones mínimas no pueden ser negativas');
      return;
    }

    if (maxSelections < minSelections) {
      toast.error('Las selecciones máximas deben ser mayor o igual a las mínimas');
      return;
    }

    if (maxVariance < minVariance) {
      toast.error('La varianza máxima debe ser mayor o igual a la mínima');
      return;
    }

    if (minVariance < 0) {
      toast.error('La varianza mínima no puede ser negativa');
      return;
    }

    if (maxVariance > maxSelections) {
      toast.error('La varianza máxima no puede ser mayor que las selecciones máximas');
      return;
    }

    if (items.length === 0) {
      toast.error('Debe agregar al menos un item al grupo');
      return;
    }

    // Final duplicate guard (should be caught earlier by UI) - prevent saving if duplicate exists
    const normalizedNew = normalize(groupName.trim());
    const duplicate = existingGroups.some((g: any) => {
      const gName = normalize(g.selection_group_name || g.name || '');
      // If in edit mode, allow same name for the group being edited
      if (editMode && editData && String(g.selection_group_id) === String(editData.id)) return false;
      return gName === normalizedNew;
    });
    if (duplicate) {
      toast.error('Ya existe un grupo con ese nombre en este local');
      return;
    }

    const selectionGroup: SelectionGroupData = {
      id: editMode && editData ? editData.id : `sg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: groupName.trim(),
      min_selections: minSelections,
      max_selections: maxSelections,
      min_variance: minVariance,
      max_variance: maxVariance,
      items
    };

    onSave(selectionGroup);
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="create-selection-group-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            {editMode ? 'Editar Grupo de Selección' : 'Crear Grupo de Selección'}
          </Modal.Title>
          <p className="form-modal-subtitle">
            Item base: <span className="current-item">{currentItemName}</span>
          </p>
        </div>
        <Button variant="none" className="modal-close-button" onClick={onHide}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <div className="selection-group-form">
          {/* Duplicate name alert (real-time) */}
          {isDuplicateName && (
            <Alert variant="danger">
              Ya existe un grupo con ese nombre en este local. Cambia el nombre para continuar.
            </Alert>
          )}
          {/* Group Configuration */}
          <GroupConfigurationSection
            groupName={groupName}
            setGroupName={setGroupName}
            minSelections={minSelections}
            setMinSelections={setMinSelections}
            maxSelections={maxSelections}
            setMaxSelections={setMaxSelections}
            minVariance={minVariance}
            setMinVariance={setMinVariance}
            maxVariance={maxVariance}
            setMaxVariance={setMaxVariance}
            itemsCount={items.length}
          />

          {/* Item Selector */}
          <ItemSelectorSection
            showItemSelector={showItemSelector}
            setShowItemSelector={setShowItemSelector}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredItems={filteredItems}
            loadingItems={loadingItems}
            onItemClick={handleAddItem}
            onMultipleItemsAdd={handleAddMultipleItems}
            itemsCount={items.length}
            allCategories={allCategories}
            selectedCategories={selectedCategories}
            onCategoriesChange={setSelectedCategories}
            onAddAllFromCategory={handleAddAllFromCategory}
            availableItems={availableItems}
          />

          {/* Items Table */}
          <ItemsTableSection
            items={items}
            onUpdateItem={handleUpdateItem}
            onDeleteItem={handleDeleteItem}
            onBulkUpdateItem={handleBulkUpdateItem}
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" onClick={onHide}>
            Cancelar
          </Button>
          <Button 
            className="form-button-success" 
            onClick={handleSave}
            disabled={!groupName.trim() || items.length === 0 || isDuplicateName}
          >
            {editMode ? 'Actualizar Grupo de Selección' : 'Crear Grupo de Selección'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateSelectionGroupModal;
